import { Link } from 'react-router-dom';

export default function ContentSection() {
  return (
    <>
      <section id="what-is" className="w-full py-16 md:py-20 bg-background-50">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100/80 text-accent-700 text-xs font-medium mb-4">
              Learn the Basics
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              What Is <span className="text-primary-500 italic">Hipobuy Spreadsheet</span>?
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
              A comprehensive resource that transforms how savvy shoppers discover, compare, and select fashion products
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
            <div className="space-y-5 text-foreground-600 text-sm md:text-base leading-relaxed">
              <p>
                A <strong className="text-foreground-900">hipobuy spreadsheet</strong> is much more than a simple product list — 
                it is a meticulously organized digital resource that compiles thousands of fashion items across multiple categories 
                into one accessible format. Savvy online shoppers use these spreadsheets to streamline their product research, 
                compare prices across different sellers, and discover trending items before they hit mainstream retail channels.
              </p>
              <p>
                The beauty of using a hipobuy spreadsheet lies in its efficiency. Instead of jumping between dozens of browser 
                tabs and manually tracking product details, prices, and seller ratings, everything you need sits in one organized, 
                searchable document. Each entry typically includes product images, detailed specifications, current pricing data, 
                seller reputation scores, shipping estimates, and quality check notes — all verified and updated regularly by 
                a community of experienced buyers who know exactly what to look for.
              </p>
              <p>
                For beginners just getting started with fashion discovery platforms, a hipobuy spreadsheet serves as the perfect 
                entry point. It eliminates the overwhelming feeling of browsing unstructured marketplaces and provides a clear, 
                guided path to finding exactly what you need. Whether you are hunting for the latest sneaker release, a specific 
                streetwear brand, or high-quality accessories at competitive prices, the spreadsheet format lets you filter, sort, 
                and compare products with unprecedented speed and accuracy.
              </p>
              <Link
                to="/hipobuy-spreadsheet-guide"
                className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors duration-200 cursor-pointer"
              >
                Read the Complete Guide
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/60">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                Traditional Search vs Spreadsheet Research
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left py-3 pr-4 font-medium text-foreground-700">Factor</th>
                      <th className="text-left py-3 pr-4 font-medium text-foreground-400">Traditional Search</th>
                      <th className="text-left py-3 font-medium text-accent-600">Spreadsheet Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Product Discovery', 'Scattered across tabs', 'All in one document'],
                      ['Price Comparison', 'Manual note-taking', 'Side-by-side columns'],
                      ['Quality Data', 'Unreliable reviews', 'Verified QC notes'],
                      ['Time Investment', 'Hours of browsing', 'Minutes of scanning'],
                      ['Update Frequency', 'Outdated listings', 'Community-refreshed daily'],
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-background-200/40">
                        <td className="py-3 pr-4 font-medium text-foreground-700">{row[0]}</td>
                        <td className="py-3 pr-4 text-foreground-500">{row[1]}</td>
                        <td className="py-3 text-accent-700 font-medium">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="benefits" className="w-full py-16 md:py-20 bg-background-100">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              Why Choose Us
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              Why Thousands Use <span className="text-accent-500">Hipobuy Spreadsheet</span>
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              Our spreadsheet resources help buyers make smarter, faster, and safer purchasing decisions
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-search-eye-line', title: 'Product Discovery', desc: 'Uncover trending items and hidden gems across dozens of categories with organized, filterable spreadsheet views that save hours of manual browsing.' },
              { icon: 'ri-layout-grid-line', title: 'Category Organization', desc: 'Navigate through 14 logically grouped product categories — from sneakers to accessories — each with dedicated columns for price, rating, and availability.' },
              { icon: 'ri-money-dollar-circle-line', title: 'Price Research', desc: 'Compare prices across multiple sellers at a glance using spreadsheet side-by-side columns, ensuring you never overpay for any item.' },
              { icon: 'ri-shield-check-line', title: 'Quality Checking', desc: 'Access community-verified QC notes, material specifications, and detailed product photos directly within each spreadsheet entry.' },
              { icon: 'ri-book-open-line', title: 'Buyer Education', desc: 'Learn shopping best practices, sizing tips, and seller evaluation strategies from experienced community members who contribute to the spreadsheet.' },
              { icon: 'ri-refresh-line', title: 'Regular Updates', desc: 'Benefit from a living document that gets refreshed with new products, updated prices, and latest trend data by an active community of contributors.' },
            ].map((item, i) => (
              <div key={i} className="bg-background-50 rounded-lg p-6 border border-background-200/60 hover:border-accent-200/60 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-to" className="w-full py-16 md:py-20 bg-background-50">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
              Getting Started
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              How To Use <span className="text-primary-500 italic">Hipobuy Spreadsheet</span>
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              Follow these five simple steps to transform your shopping experience
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-background-200 -translate-y-1/2 z-0"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 relative z-10">
              {[
                { step: '01', title: 'Find Spreadsheet', desc: 'Access the latest hipobuy spreadsheet resource through our organized category portal.' },
                { step: '02', title: 'Browse Categories', desc: 'Navigate to your desired product category — shoes, jackets, accessories, and more.' },
                { step: '03', title: 'Compare Products', desc: 'Use side-by-side columns to evaluate prices, ratings, and specifications across sellers.' },
                { step: '04', title: 'Review Information', desc: 'Read community QC notes, check material details, and verify seller reputation scores.' },
                { step: '05', title: 'Make Decisions', desc: 'Confidently select the best product based on comprehensive spreadsheet data and insights.' },
              ].map((s, i) => (
                <div key={i} className="text-center bg-background-50 rounded-lg p-5 border border-background-200/60 relative">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-500 text-white flex items-center justify-center font-heading text-lg font-bold">
                    {s.step}
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">{s.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="articles" className="w-full py-16 md:py-20 bg-background-100">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100/80 text-accent-700 text-xs font-medium mb-4">
              In-Depth Resources
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              Explore Our <span className="text-accent-500">SEO Guides</span>
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              Deep-dive articles covering everything from beginner basics to advanced platform comparisons
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <Link
              to="/hipobuy-spreadsheet-guide"
              className="bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 overflow-hidden cursor-pointer group"
            >
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20workspace%20flat%20lay%20with%20an%20open%20notebook%20showing%20handwritten%20shopping%20research%20notes%20next%20to%20a%20laptop%20displaying%20spreadsheet%20data%2C%20warm%20ambient%20lighting%20from%20desk%20lamp%2C%20cozy%20productive%20atmosphere%2C%20modern%20home%20office%20aesthetic%2C%20soft%20focus%20background%20with%20plant%20silhouette%2C%20clean%20and%20organized%20creative%20workspace&width=600&height=400&seq=articles-beginner-v2&orientation=landscape"
                  alt="Beginner's guide to spreadsheet shopping"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 to-transparent"></div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Beginner</span>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
                  Complete Beginner&apos;s Guide 2026
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">Everything you need to start using spreadsheet research for smarter shopping — from setup to advanced techniques.</p>
              </div>
            </Link>

            <Link
              to="/spreadsheet-platform-comparison"
              className="bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 overflow-hidden cursor-pointer group"
            >
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Top%20down%20view%20of%20five%20distinct%20product%20photography%20setups%20side%20by%20side%20on%20a%20light%20wooden%20table%2C%20each%20with%20different%20fashion%20items%20arranged%20in%20their%20own%20style%2C%20clean%20studio%20lighting%2C%20editorial%20comparison%20concept%2C%20modern%20minimalist%20composition%2C%20diverse%20product%20showcase%20aesthetic&width=600&height=400&seq=articles-comparison-v2&orientation=landscape"
                  alt="Spreadsheet platform comparison"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 to-transparent"></div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">Comparison</span>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
                  Platform Comparison: 5 Spreadsheets
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">Hipobuy vs Kakobuy vs CSSBuy vs Superbuy vs Oopbuy — an unbiased deep-dive into features, QC quality, and value.</p>
              </div>
            </Link>

            <Link
              to="/spreadsheet-category-guide"
              className="bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 overflow-hidden cursor-pointer group"
            >
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Creative%20grid%20arrangement%20of%20fourteen%20small%20product%20vignettes%20each%20featuring%20a%20different%20fashion%20item%20category%20on%20soft%20ivory%20background%2C%20organized%20editorial%20layout%2C%20clean%20consistent%20studio%20lighting%2C%20modern%20catalog%20design%20concept%2C%20harmonious%20color%20coordination%20across%20all%20items&width=600&height=400&seq=articles-category-v2&orientation=landscape"
                  alt="Category deep-dive guide"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 to-transparent"></div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">Categories</span>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
                  14 Categories Deep-Dive Guide
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">Master every product category with targeted research tips, what to look for, and category-specific shopping strategies.</p>
              </div>
            </Link>

            <Link
              to="/safe-shopping-guide"
              className="bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 overflow-hidden cursor-pointer group"
            >
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20composition%20featuring%20a%20protective%20glass%20dome%20covering%20a%20small%20fashion%20item%20with%20soft%20golden%20rim%20lighting%2C%20symbolic%20safety%20and%20protection%20concept%2C%20elegant%20product%20photography%20on%20light%20sand%20surface%2C%20refined%20studio%20aesthetic%2C%20conceptual%20editorial%20style&width=600&height=400&seq=articles-safety-v2&orientation=landscape"
                  alt="Safe shopping guide"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 to-transparent"></div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Safety</span>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
                  Safe Shopping Guide
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">Protect yourself with the five-pillar safety framework — seller verification, QC analysis, payment protection, and scam avoidance.</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section id="guide-preview" className="w-full py-16 md:py-20 bg-background-50">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <div className="bg-background-100 rounded-lg border border-background-200/60 p-8 md:p-10">
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100/80 text-accent-700 text-xs font-medium mb-4">
                  In-Depth Guide
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  Hipobuy Spreadsheet: Complete Beginner&apos;s Guide 2026
                </h2>
                <p className="text-foreground-500 text-sm leading-relaxed mb-4">
                  Ready to master the art of spreadsheet-powered shopping? Our comprehensive guide covers everything 
                  from setting up your first spreadsheet workflow to advanced product research techniques used by 
                  experienced buyers. Learn how to decode QC terminology, interpret seller ratings accurately, 
                  and build your own optimized shopping strategy that saves both time and money.
                </p>
                <p className="text-foreground-500 text-sm leading-relaxed mb-6">
                  This in-depth resource walks you through real-world examples, provides comparison frameworks, 
                  and shares insider tips that most beginners miss. Whether you are brand new to product discovery 
                  spreadsheets or looking to refine your existing research methods, this guide has practical, 
                  actionable advice you can start using today.
                </p>
                <Link
                  to="/hipobuy-spreadsheet-guide"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Read the Full Guide
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="w-full lg:w-64 shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Overhead%20shot%20of%20an%20organized%20desk%20with%20a%20tablet%20displaying%20colorful%20spreadsheet%20data%20next%20to%20fashion%20magazines%20and%20a%20cup%20of%20coffee%2C%20warm%20morning%20sunlight%20streaming%20through%20window%2C%20creative%20productive%20lifestyle%20photography%2C%20modern%20content%20creator%20workspace%2C%20soft%20bokeh%20background&width=512&height=640&seq=guide-preview-v2-02&orientation=portrait"
                  alt="Hipobuy Spreadsheet guide preview"
                  className="w-full h-64 lg:h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="safety" className="w-full py-16 md:py-20 bg-background-50">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              Trust &amp; Safety
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              How We Help You <span className="text-accent-500">Shop Safer</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: 'ri-file-search-line', text: 'Research before buying with verified product data and community insights' },
              { icon: 'ri-check-double-line', text: 'Verify product details against multiple sources and seller listings' },
              { icon: 'ri-user-star-line', text: 'Understand seller reputation through aggregated rating data and reviews' },
              { icon: 'ri-lock-line', text: 'Protect personal information with recommended secure shopping practices' },
              { icon: 'ri-alert-line', text: 'Avoid risky decisions by learning common pitfalls and warning signs' },
            ].map((item, i) => (
              <div key={i} className="text-center p-5">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <p className="text-xs text-foreground-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/safe-shopping-guide"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-foreground-200 hover:border-primary-300 text-foreground-700 font-medium text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Read the Complete Safety Guide
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}